import {
  Choice,
  DependentsChoice,
  EeClassWithValidation,
  INITIAL_CHOICE,
  ProductInfo,
  RateStatistic,
  RequiredSubInfo,
  RequiredSubInfoPresenter,
  SavingStatus,
  StatisticsValues,
  SubChecklistCard,
  Subscriber,
  UpdatedPlanInfo,
} from 'types/electionService';
import { createRequiredSubInfoPresenter } from 'global/utils/election/electionPresenters';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { INITIAL_STATISTIC } from 'store/domain/electionStore';
import EnrollmentPeriodModule from 'store/modules/enrollment/enrollmentPeriodModule';
import { RootStore } from 'store/rootStore';
import {
  completeSubscriberChecklistStep,
  getEmployeeClasses,
  getProducts,
  getRequiredSubInfo,
  getStatistics,
  getSubscriberWithChoices,
  updateSubscribers,
} from 'api/operations/electionService';
import { getTaskByEntityId, sortDependentsInSubscriber } from 'global/services-helpers/electionService';
import { getGroupById } from 'api/operations/groupService';
import { Group } from 'types/groupService';
import { bpHistory } from 'global/bpHistory';
import { getSubscriberTaskChecklistURL } from 'global/checklists-helpers/SubscriberChecklistHelper';
import { ChecklistOption, ChecklistCompletionStatus } from '../../../types/enrollmentService';
import EditPrimaryModalStore from './editPrimaryModalStore';
import ManageDependentsModalStore from './manageDependentsStore';

// TODO refactor store to use new approaches before covering with tests
export default class ManageSubscriberStore {
  rootStore: RootStore;

  private _group: Pick<Group, 'name' | 'id' | 'contactInfo'> | null = null;
  private _enrollmentPeriodModule = new EnrollmentPeriodModule();

  public loading = false;
  public subscriber: Subscriber | null = null;
  public products: ProductInfo[] = [];
  public employeeClasses: EeClassWithValidation[] = [];
  public savingStatus: SavingStatus = 'SUCCESSFUL_SAVING';
  public rateStats: RateStatistic | null = null;
  public statistic: StatisticsValues = INITIAL_STATISTIC;
  public requiredSubInfo: RequiredSubInfo | null = null;

  public editPrimaryModalStore: EditPrimaryModalStore;
  public manageDependentsModalStore: ManageDependentsModalStore;

  public get group(): Pick<Group, 'name' | 'id' | 'contactInfo'> | null {
    return this._group;
  }

  private get _periodId(): string {
    return this._enrollmentPeriodModule.enrollmentCoreData?.id || '';
  }

  public get taskChecklistOptions(): ChecklistOption<SubChecklistCard>[] {
    return this.subscriber ? getTaskByEntityId(this.subscriber, this._periodId)?.checklist?.checklistOptions || [] : [];
  }

  public get isStepCompleted(): boolean {
    return (
      this.taskChecklistOptions.find((option) => option.card === SubChecklistCard.MANAGE_SUBSCRIBER)
        ?.completionStatus === ChecklistCompletionStatus.COMPLETED
    );
  }

  /**
   * Enrollment required-subscriber rules for SubscriberCard and validation (pages without ElectionsModule).
   */
  public get requiredSubInfoPresenter(): RequiredSubInfoPresenter | undefined {
    return createRequiredSubInfoPresenter(toJS(this.requiredSubInfo), 'enrollment') ?? undefined;
  }

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private setSavingStatus(status: SavingStatus): void {
    this.savingStatus = status;
  }

  public async getInitialData(periodId: string, subId: string, groupId: string): Promise<void> {
    this.resetInitialData();
    this.setLoading(true);

    const [group] = await Promise.all([
      getGroupById(groupId),
      this._enrollmentPeriodModule.uploadEnrollmentPeriod(periodId),
      this._getProducts(periodId),
      this._getRequiredSubInfo(),
      this._getClasses(groupId, periodId),
      this.getStatistic(groupId, periodId),
    ]);

    void runInAction(async (): Promise<void> => {
      this._group = group ? { name: group.name, id: group.id, contactInfo: group.contactInfo } : null;

      await this.getSubscriber(subId);
      // Temporary solution to support the legacy subscriber module (subscriberStore.ts)
      // that is still used in subscriber modals.
      // Can be removed once the new subscriber and election delegates are used here.
      this.rootStore.subscriberStore.setPeriodEditableStatus(this._enrollmentPeriodModule.enrollmentPeriod);
      this.setLoading(false);
    });
  }

  // Get info
  public async getSubscriber(subId: string): Promise<void> {
    const subscriber = await getSubscriberWithChoices(subId, this._periodId);
    runInAction(() => {
      const convertedProducts = this.products.map((product) => toJS(product)).slice();

      if (subscriber.choice.length === convertedProducts.length) {
        this.subscriber = { ...sortDependentsInSubscriber(subscriber), periodId: this._periodId };
      } else {
        this.subscriber = {
          ...sortDependentsInSubscriber(subscriber),
          periodId: this._periodId,
          choice: this.createInitialChoicesList(subscriber),
        };
      }
    });
  }

  private async _getProducts(periodId: string): Promise<void> {
    const products = await getProducts(periodId);
    runInAction(() => {
      this.products = products;
    });
  }

  private async _getClasses(groupId: string, periodId: string): Promise<void> {
    const classes = await getEmployeeClasses(groupId, periodId);
    runInAction(() => {
      this.employeeClasses = classes;
    });
  }

  private async _getRequiredSubInfo(): Promise<void> {
    const domainId = this.rootStore.domainStoreV2.domainHeader.id;
    const requiredSubInfo = await getRequiredSubInfo(domainId);
    runInAction(() => {
      this.requiredSubInfo = requiredSubInfo;
    });
  }

  public async getStatistic(groupId: string, periodId: string): Promise<void> {
    const res = await getStatistics(groupId, periodId);
    runInAction(() => {
      const { rateStats, ...restStatistic } = res;
      this.statistic = restStatistic;
      this.rateStats = rateStats;
    });
  }

  // Update info
  public updateDependentsChoice(dependentChoice: DependentsChoice): void {
    const convertedSubscriber = toJS(this.subscriber);
    const { productLineId, dependentIds } = dependentChoice;

    if (convertedSubscriber) {
      const convertedChoices = convertedSubscriber.choice.map((choice) => {
        if (choice.productLineId === productLineId) {
          return { ...choice, dependentIds };
        }
        return choice;
      });

      this.subscriber = { ...convertedSubscriber, choice: convertedChoices };
      void this.updateElectionChanges();
    }
  }

  public updateProduct({ productLineId, selectedPlanName, selectedPlanId }: UpdatedPlanInfo): void {
    const convertedSubscriber = toJS(this.subscriber);

    if (convertedSubscriber) {
      const convertedChoices = convertedSubscriber.choice.map((choice) => {
        if (choice.productLineId === productLineId) {
          return { ...choice, productId: selectedPlanId, productName: selectedPlanName };
        }
        return choice;
      });

      this.subscriber = { ...convertedSubscriber, choice: convertedChoices };
      void this.updateElectionChanges();
    }
  }

  public editPrimaryInfo(subscriberId: string): void {
    const formId = this.requiredSubInfo?.editSubFormId;
    if (!formId) {
      throw new Error('Edit primary form ID is missing');
    }
    void this.editPrimaryModalStore.openEditPrimaryModal(subscriberId, formId);
  }

  public manageDependents(subscriberId: string): void {
    const formId = this.requiredSubInfo?.manageDependentFormId;
    if (!formId) {
      throw new Error('Manage dependents form ID is missing');
    }
    void this.manageDependentsModalStore.openManageDependentsModal(subscriberId, formId);
  }

  public async completeElectionChangeStatus(subId: string): Promise<void> {
    this.setLoading(true);
    await completeSubscriberChecklistStep({
      subId,
      entityId: this._periodId,
      card: SubChecklistCard.MANAGE_SUBSCRIBER,
    });
    runInAction(() => {
      this.setLoading(false);
    });
  }

  public async updateElectionChanges(): Promise<void> {
    this.setSavingStatus('SAVING');
    const convertedSubscriber = toJS(this.subscriber);
    if (convertedSubscriber) {
      const groupId = this._group?.id || '';
      const periodId = this._periodId;
      try {
        await updateSubscribers(periodId, [convertedSubscriber]);
        void this.getStatistic(groupId, periodId);
        this.setSavingStatus('SUCCESSFUL_SAVING');
      } catch {
        this.setSavingStatus('FAILED_SAVING');
      }
    }
  }

  // Helpers
  private createInitialChoicesList(subscriber: Subscriber): Choice[] {
    const defaultDependentsList = subscriber.custom.dependents.map(({ id }) => id) || [];

    const defaultInitialChoicesList: Choice[] =
      this.products?.map(({ productLineId }) => {
        // productLine with 1 tier needs dependentIds as empty array
        const hasProductLineOneTier = Object.keys(this.rateStats?.[productLineId] || []).length === 1;
        return { ...INITIAL_CHOICE, productLineId, dependentIds: hasProductLineOneTier ? [] : defaultDependentsList };
      }) || [];

    return defaultInitialChoicesList;
  }

  public resetInitialData(): void {
    this.subscriber = null;
    this.statistic = INITIAL_STATISTIC;
  }

  public get cartId(): string {
    return this._enrollmentPeriodModule.enrollmentProposalData?.confirmedProposal?.cartId || '';
  }

  public onBackRedirect(): void {
    bpHistory.push(
      getSubscriberTaskChecklistURL({
        entityId: this._periodId,
        subId: this.subscriber?.id || '',
        groupId: this._group?.id || '',
      }),
    );
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.editPrimaryModalStore = new EditPrimaryModalStore(this.rootStore);
    this.manageDependentsModalStore = new ManageDependentsModalStore(this.rootStore);
    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
  }
}
